import React from 'react';
import { useIntl } from 'react-intl';
import { useLanguage } from '../../context';
import { getLocalizedPath } from '../../helpers/routes';

const NewsSection = ({ news = null, blogs = [] }) => {
  const intl = useIntl();
  const { language } = useLanguage();

  const buildLink = (item) => {
    if (!item) return '#';
    if (item.type && item.type.toLowerCase().includes('blog')) {
      return getLocalizedPath(`/blog/${item.slug}`, language);
    }
    if (item.type && item.type.toLowerCase().includes('news')) {
      return getLocalizedPath(`/news/${item.slug}`, language);
    }
    if (item.isBlog) {
      return getLocalizedPath(`/blog/${item.slug}`, language);
    }
    return getLocalizedPath(`/news/${item.slug}`, language);
  };

  const displayNews =
    typeof news === 'object' && news !== null && 'bigEvent' in news
      ? news.bigEvent
      : news;
  const displayTopNews =
    typeof news === 'object' && news !== null && 'topNews' in news ? news.topNews : news;
  const displayBottomItems =
    typeof news === 'object' && news !== null && 'bottomItems' in news
      ? news.bottomItems
      : blogs.length > 0
        ? blogs
        : [];

  const NewsTile = ({
    item,
    className = '',
    titleClass = 'text-2xl',
    overlay = 'from-[#0a1628]/80'
  }) => {
    if (!item) return null;
    return (
      <a
        href={buildLink(item)}
        className={`relative overflow-hidden group min-h-[240px] flex flex-col justify-end ${className}`}
      >
        <div
          className="absolute inset-0 bg-slate-300/40 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-[1.04]"
          style={{ backgroundImage: `url(${item.image})` }}
        />
        <div
          className={`absolute inset-0 bg-gradient-to-t ${overlay} via-[#0a1628]/25 to-transparent`}
        />
        <div className="relative z-10 p-6 md:p-8 text-white">
          {item.type ? (
            <span className="inline-block bg-[#16A34A] text-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] mb-3">
              {item.type}
            </span>
          ) : null}
          {item.subtitle ? (
            <p className="text-sm text-white/70 mb-1 line-clamp-1">{item.subtitle}</p>
          ) : null}
          <h3 className={`font-bold leading-tight ${titleClass}`}>{item.title}</h3>
          <span className="inline-flex items-center gap-2 mt-4 text-xs font-semibold uppercase tracking-[0.14em] text-transparent group-hover:text-white transition-colors duration-300">
            {intl.formatMessage({ id: 'learn.more', defaultMessage: 'Learn More' })}
            <span aria-hidden>→</span>
          </span>
        </div>
      </a>
    );
  };

  return (
    <section className="bg-white py-20 lg:py-28">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="mb-12 lg:mb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-px bg-[#16A34A]" />
            <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#16A34A]">
              Actualités
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0a1628] tracking-tight max-w-2xl">
            {intl.formatMessage({
              id: 'news.section.title',
              defaultMessage: 'Latest News & Updates'
            })}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
          <NewsTile
            item={displayNews}
            className="lg:col-span-2 min-h-[380px] lg:min-h-[480px]"
            titleClass="text-3xl md:text-4xl lg:text-5xl"
            overlay="from-[#0a1628]/90"
          />
          <NewsTile
            item={displayTopNews}
            className="min-h-[280px] lg:min-h-full"
            titleClass="text-xl md:text-2xl"
          />
        </div>

        {displayBottomItems.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mt-3 md:mt-4">
            {displayBottomItems.map((item, index) => (
              <NewsTile key={index} item={item} titleClass="text-lg md:text-xl" />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default NewsSection;
